// LogsOverview - Category filter pills with inline dropdowns
import {
  CheckIcon,
  CommandLineIcon,
  UserIcon,
  UserGroupIcon,
  CpuChipIcon,
  ShieldExclamationIcon,
  ServerIcon,
  SignalIcon,
  CogIcon,
  ChevronDownIcon,
  ComputerDesktopIcon,
  DevicePhoneMobileIcon,
} from '@heroicons/react/24/outline';

// Category pill with icon - toggle behavior
function CategoryPill({ label, count, color, icon: Icon, selected, onClick, hasSubFilters }) {
  const baseColors = {
    purple: { bg: 'bg-purple-100', text: 'text-purple-700', selected: 'bg-purple-600 text-white' },
    blue: { bg: 'bg-blue-100', text: 'text-blue-700', selected: 'bg-blue-600 text-white' },
    cyan: { bg: 'bg-cyan-100', text: 'text-cyan-700', selected: 'bg-cyan-600 text-white' },
    green: { bg: 'bg-green-100', text: 'text-green-700', selected: 'bg-green-600 text-white' },
    red: { bg: 'bg-red-100', text: 'text-red-700', selected: 'bg-red-600 text-white' },
    orange: { bg: 'bg-orange-100', text: 'text-orange-700', selected: 'bg-orange-600 text-white' },
    indigo: { bg: 'bg-indigo-100', text: 'text-indigo-700', selected: 'bg-indigo-600 text-white' },
    gray: { bg: 'bg-gray-100', text: 'text-gray-700', selected: 'bg-gray-600 text-white' },
  };

  const colors = baseColors[color] || baseColors.gray;

  return (
    <button
      onClick={onClick}
      className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${
        selected ? colors.selected : `${colors.bg} ${colors.text} hover:opacity-80`
      }`}
    >
      {selected ? <CheckIcon className="w-4 h-4" /> : <Icon className="w-4 h-4" />}
      <span>{label}</span>
      <span className="font-bold">{count.toLocaleString()}</span>
      {hasSubFilters && selected && <ChevronDownIcon className="w-3 h-3 ml-0.5" />}
    </button>
  );
}

// WebSocket endpoint sub-filter pill
function WsEndpointPill({ label, count, icon: Icon, selected, onClick }) {
  return (
    <button
      onClick={onClick}
      className={`flex items-center gap-1 px-2 py-1 rounded text-xs font-medium transition-all ${
        selected
          ? 'bg-indigo-500 text-white'
          : 'bg-indigo-50 text-indigo-600 hover:bg-indigo-100'
      }`}
    >
      <Icon className="w-3 h-3" />
      <span>{label}</span>
      <span className="font-bold">{count}</span>
    </button>
  );
}

// Inline dropdown component
function InlineDropdown({ value, onChange, options, label }) {
  return (
    <div className="relative">
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="appearance-none bg-gray-100 border-0 rounded-lg pl-3 pr-7 py-1.5 text-sm font-medium text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500 cursor-pointer"
      >
        {options.map((opt) => (
          <option key={opt.value} value={opt.value}>{opt.label}</option>
        ))}
      </select>
      <ChevronDownIcon className="w-3.5 h-3.5 absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
    </div>
  );
}

// Level options
const levelOptions = [
  { value: '', label: 'All Levels' },
  { value: 'audit', label: 'Audit' },
  { value: 'info', label: 'Info' },
  { value: 'warn', label: 'Warning' },
  { value: 'error', label: 'Error' },
];

// Limit options
const limitOptions = [
  { value: 50, label: '50 results' },
  { value: 100, label: '100 results' },
  { value: 200, label: '200 results' },
  { value: 500, label: '500 results' },
];

// WebSocket event type options
const wsEventOptions = [
  { value: '', label: 'All Events' },
  { value: 'connect', label: 'Connect' },
  { value: 'disconnect', label: 'Disconnect' },
  { value: 'auth_success', label: 'Auth Success' },
  { value: 'auth_failure', label: 'Auth Failed' },
  { value: 'subscribe', label: 'Subscribe' },
  { value: 'unsubscribe', label: 'Unsubscribe' },
  { value: 'switch_topic', label: 'Switch Topic' },
  { value: 'rate_limit', label: 'Rate Limit' },
  { value: 'timeout', label: 'Timeout' },
  { value: 'error', label: 'Error' },
];

// WebSocket filter options - by actor type (more useful than endpoint)
// device = IoT device connections
// employee = Employee dashboard connections (IoT Dashboard)
// user = User dashboard connections (User Dashboard)
const wsActorTypes = [
  { key: 'device', label: 'Devices', icon: CpuChipIcon },
  { key: 'employee', label: 'Employees', icon: ComputerDesktopIcon },
  { key: 'user', label: 'Users', icon: DevicePhoneMobileIcon },
];

export default function LogsOverview({
  stats,
  wsStats,
  onCategoryToggle,
  selectedCategories = [],
  level = '',
  onLevelChange,
  limit = 100,
  onLimitChange,
  wsActorType = '',
  onWsActorTypeChange,
  wsEvent = '',
  onWsEventChange,
}) {
  const categories = [
    { key: 'ssh', label: 'SSH', color: 'purple', icon: CommandLineIcon },
    { key: 'employee', label: 'Employee', color: 'blue', icon: UserIcon },
    { key: 'user', label: 'User', color: 'cyan', icon: UserGroupIcon },
    { key: 'device', label: 'Device', color: 'green', icon: CpuChipIcon },
    { key: 'api', label: 'API', color: 'orange', icon: ServerIcon },
    { key: 'security', label: 'Security', color: 'red', icon: ShieldExclamationIcon },
    { key: 'ws', label: 'WebSocket', color: 'indigo', icon: SignalIcon, hasSubFilters: true },
    { key: 'system', label: 'System', color: 'gray', icon: CogIcon },
  ];

  const isWsSelected = selectedCategories.includes('ws');
  const onlyWsSelected = selectedCategories.length === 1 && isWsSelected;

  return (
    <div className="space-y-2">
      {/* Main Row: Category Pills + Dropdowns */}
      <div className="flex flex-wrap items-center gap-2">
        {/* Category Pills - click to toggle */}
        {categories.map(cat => {
          const count = stats?.byCategory?.[cat.key] || 0;
          if (count === 0) return null;
          return (
            <CategoryPill
              key={cat.key}
              label={cat.label}
              count={count}
              color={cat.color}
              icon={cat.icon}
              selected={selectedCategories.includes(cat.key)}
              onClick={() => onCategoryToggle(cat.key)}
              hasSubFilters={cat.hasSubFilters}
            />
          );
        })}

        {/* Separator */}
        <div className="h-6 w-px bg-gray-300 mx-1" />

        {/* Level Dropdown */}
        <InlineDropdown
          value={level}
          onChange={onLevelChange}
          options={levelOptions}
        />

        {/* Limit Dropdown */}
        <InlineDropdown
          value={limit}
          onChange={(val) => onLimitChange(parseInt(val))}
          options={limitOptions}
        />

        {/* Show selected count */}
        {selectedCategories.length > 0 && (
          <span className="text-sm text-gray-500 ml-2">
            ({selectedCategories.length} selected)
          </span>
        )}

        {/* Show message if no categories have data */}
        {categories.every(cat => (stats?.byCategory?.[cat.key] || 0) === 0) && (
          <span className="text-sm text-gray-400">No log categories available</span>
        )}
      </div>

      {/* WebSocket Sub-Filters (shown when WS is selected) */}
      {onlyWsSelected && wsStats && (
        <div className="flex flex-wrap items-center gap-2 pl-1 pt-1 border-t border-gray-100">
          <span className="text-xs text-gray-500 mr-1">Type:</span>

          {/* All Types */}
          <WsEndpointPill
            label="All"
            count={wsStats.total || 0}
            icon={SignalIcon}
            selected={!wsActorType}
            onClick={() => onWsActorTypeChange('')}
          />

          {/* Actor type pills - Devices, Employees, Users */}
          {wsActorTypes.map(at => {
            // Get count from byActorType stats
            const count = wsStats.byActorType?.[at.key] || 0;
            if (count === 0) return null;
            return (
              <WsEndpointPill
                key={at.key}
                label={at.label}
                count={count}
                icon={at.icon}
                selected={wsActorType === at.key}
                onClick={() => onWsActorTypeChange(at.key)}
              />
            );
          })}

          {/* Separator */}
          <div className="h-4 w-px bg-gray-200 mx-1" />

          {/* Event Type Dropdown */}
          <span className="text-xs text-gray-500 mr-1">Event:</span>
          <InlineDropdown
            value={wsEvent}
            onChange={onWsEventChange}
            options={wsEventOptions}
          />

          {/* Quick Stats */}
          {wsStats.metrics && (
            <>
              <div className="h-4 w-px bg-gray-200 mx-1" />
              <div className="flex items-center gap-2 text-xs">
                {wsStats.metrics.securityEvents > 0 && (
                  <span className="text-red-600">
                    {wsStats.metrics.securityEvents} security events
                  </span>
                )}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}
